/* Copyright (c) 2020-present CloudIO Inc */

import { isEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn, AliasType } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './UserList';

const UserListManager: ComponentManagerFn<'UserList'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'UserList'>(platform, 'UserList'),
    type: 'UserList',
    name: 'User List',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        allowedTypes: [
          'CloudDataStore',
          'ReadOnlyDataStore',
          'LocalDataStore',
          'DataStore',
        ],
        required: true,
      },
      userName: {
        label: 'User Name',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        required: true,
        getAttributeDataTypes: () => ['String'],
      },
      maxUsers: {
        label: 'Max Users',
        type: 'number',
        min: 4,
        max: 20,
        helpText: 'specify a count to display a max number of users.',
      },
      size: {
        label: 'Size',
        type: 'number',
        min: 24,
        max: 40,
        step: 4,
      },
      displayAsStack: {
        label: 'Display As Stack',
        type: 'boolean',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'userName':
          return !isEmpty(itemData.alias);
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'alias' } : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    category: 'Forms',
    icon: { iconName: 'users' },
    description: `**Overview**<br/>
The User List component provides a visual way to display a group of users, typically rendering them as a horizontal series of avatars or initials. This pattern is commonly known as a "Facepile."
Use this component to show team members, document collaborators, or a list of assignees in a compact, space-efficient manner.

**1. Data Connection**<br/>
To function, the component must be connected to a data source that contains the user records.
* **Data Store Alias:** Select the Data Store (Cloud, ReadOnly, Local) containing the list of users.
* **User Name:** Select the specific text column from that Data Store to display.
* **Usage:** This string is used to generate the user's initials if no image is available, or as the tooltip text when hovering over an avatar.
* **Supported Type:** **String**.

**2. Layout & Appearance**<br/>
You can customize how the users are arranged and sized to fit your UI density.
* **Display As Stack:** A boolean toggle.
    * **Enabled (Stack):** Avatars overlap each other slightly (like a deck of cards), saving horizontal space. This is the standard "Facepile" look.
    * **Disabled (List):** Avatars are placed side-by-side with standard spacing.
* **Size:** Controls the diameter of the user circles in pixels. (Range: 24px to 40px).
* **Max Users:** Limits the number of individual avatars rendered. (Range: 4 to 20 users).
* **Overflow:** If the data source contains more users than this limit, the component renders the specified number and summarizes the rest (e.g., a final circle saying "+5").

**3. Interactivity**<br/>
* **TriggerOnClick:** You can attach a **TriggerOnClick** child component.
* **Use Case:** Clicking the list could open a modal showing the full details of all users in the group or navigate to a team management page.

**4. Best Use Cases**<br/>
* **Task Management:** Showing which users are assigned to a specific task card in a Kanban board.
* **Collaboration:** Displaying "Who is currently viewing this document" in real-time.
* **Team Rosters:** Listing the members of a project team in a dashboard header.
* **Social Features:** Displaying mutual friends or followers in a profile summary.`,
  };
};

export default UserListManager;
